import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';

import { RightArrowSettings } from 'assets/images';

import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import { EpgLayout } from 'store/epg/reducer.types';
import { getEpgSelectedLayout } from 'store/epg/selectors';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';

import SettingsRowItem from '../components/RowItem';
import {
    SETTINGS_DISPLAY_EPG_OPTIONS,
    SETTINGS_DISPLAY_EPG_OPTIONS_CLASSIC,
    SETTINGS_DISPLAY_EPG_OPTIONS_NEW,
} from '../languagesConst';
import { SimpleSettingsItem } from '../types';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.SETTINGS>;
};

function EpgDisplay({ navigation }: Props) {
    const { t } = useTranslation();

    const { background } = useSelector(selectLayoutConfig);
    const epgLayout = useSelector(getEpgSelectedLayout);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const [selectedEpgLayout, setSelectedEpgLayout] = useState(epgLayout);

    useHeaderConfig({
        isRoot: false,
    });

    const listStyle = useMemo(
        () => ({
            backgroundColor: background,
        }),
        [background],
    );
    const displayOption = useMemo<SimpleSettingsItem>(
        () => ({
            id: SETTINGS_DISPLAY_EPG_OPTIONS,
            title: SETTINGS_DISPLAY_EPG_OPTIONS,
            subtitle:
                selectedEpgLayout === EpgLayout.GRID
                    ? SETTINGS_DISPLAY_EPG_OPTIONS_CLASSIC
                    : SETTINGS_DISPLAY_EPG_OPTIONS_NEW,
        }),
        [selectedEpgLayout],
    );

    const handleMenuItemPress = useCallback(() => {
        navigation.navigate(SCREEN.SETTINGS_EPG_DISPLAY_OPTIONS);
    }, [navigation]);

    const debouncedMenuItemPress = useRef(
        debounce(handleMenuItemPress, 500, {
            leading: true,
            trailing: false,
        }),
    );

    useEffect(() => {
        setSelectedEpgLayout(
            epgLayout ??
                (screenReaderEnabled ? EpgLayout.LIST : EpgLayout.GRID),
        );
    }, [epgLayout, screenReaderEnabled]);

    return (
        <View style={[gStyles.settingsContainer, listStyle]}>
            <View
                style={gStyles.settingsContentContainer}
                accessibilityRole="menu"
            >
                <SettingsRowItem
                    item={displayOption}
                    title={t(displayOption.title)}
                    subtitle={t(displayOption.subtitle!)}
                    onPress={debouncedMenuItemPress.current}
                    RightIcon={RightArrowSettings}
                />
            </View>
        </View>
    );
}

export default EpgDisplay;
